import { getMomentTz } from "@dexteel/mesf-core";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const moment = getMomentTz();

/**
 * Both defaults are resolved once, when the module loads, so every reader of an absent `start` or
 * `end` gets the exact same instant. Recomputing them per render would give the query a key that
 * changes on every render and a page that never stops fetching.
 */
export const DEFAULT_START: Date = (() => {
	const date = new Date();
	date.setDate(1);
	date.setMonth(0);
	date.setFullYear(new Date().getFullYear());
	return date;
})();

export const DEFAULT_END: Date = moment()
	.hour(23)
	.minute(59)
	.second(59)
	.millisecond(0)
	.toDate();

/**
 * What goes in the address bar when the parameter is missing, and what the filters read back for it.
 *
 * `toISOString`, never `toJSON`: the application replaces `Date.prototype.toJSON` with one that
 * prints server-local wall time and no zone, and what it prints changes once moment's default
 * timezone is installed. Writing the URL with it and reading it back with `moment` therefore gave
 * two different instants for the same filter, which is where both the duplicate request and the
 * two-hour drift in this page's date range came from.
 */
export const DEFAULT_START_PARAM = encodeURIComponent(
	DEFAULT_START.toISOString(),
);
export const DEFAULT_END_PARAM = encodeURIComponent(DEFAULT_END.toISOString());

export type WorkOrdersFilters = {
	/** null while the route has no usable area, which is what keeps the query from running. */
	assetId: number | null;
	start: Date;
	end: Date;
	/**
	 * The dates exactly as they sit in the URL. The query is keyed on these rather than on the parsed
	 * dates, so a value the address bar has not changed can never produce a different key.
	 */
	startParam: string;
	endParam: string;
	searchText: string;
	typeCode: string;
	statusCode: string;
	craftId: number | string;
	includeExpired: boolean;
};

/**
 * The URL keeps the dates encoded once more than the address bar does, which is how they were
 * written before this hook existed, so links people already have keep working.
 */
const readDate = (param: string, fallback: Date): Date => {
	const parsed = moment(decodeURIComponent(param));
	return parsed.isValid() ? parsed.toDate() : fallback;
};

/**
 * The filters, read straight from the URL and the route on every render. See the note in
 * `task-list/hooks/useTaskListFilters.ts`: nothing here waits for anything, and the defaults are
 * applied in the read rather than by writing them into the URL first.
 */
export const useWorkOrdersFilters = (): WorkOrdersFilters => {
	const [searchParams] = useSearchParams();
	const { AssetIdParam } = useParams();

	const start = searchParams.get("start");
	const end = searchParams.get("end");
	const search = searchParams.get("search");
	const type = searchParams.get("type");
	const status = searchParams.get("status");
	const craft = searchParams.get("craft");
	const expired = searchParams.get("expired");

	return useMemo(() => {
		const assetId = Number(AssetIdParam);
		const craftId = Number.parseInt(craft as string, 10);
		const startParam = start ?? DEFAULT_START_PARAM;
		const endParam = end ?? DEFAULT_END_PARAM;

		return {
			assetId: Number.isInteger(assetId) ? assetId : null,
			start: readDate(startParam, DEFAULT_START),
			end: readDate(endParam, DEFAULT_END),
			startParam,
			endParam,
			searchText: search ?? "",
			typeCode: type === "PM" || type === "RM" ? type : "A",
			statusCode: status === "C" || status === "A" ? status : "O",
			craftId: Number.isInteger(craftId) && craftId >= 1 ? craftId : "A",
			includeExpired: expired === "true",
		};
	}, [AssetIdParam, start, end, search, type, status, craft, expired]);
};
